/**
 * Audio Player Component
 * Optimized audio playback component with loading states, error handling, and mobile-friendly controls
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Activity, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AudioVisualizer } from './AudioVisualizer';
import {
  AUDIO_LOAD_MAX_ATTEMPTS,
  applyAudioCrossOrigin,
  audioLoadRetryDelayMs,
  isRetryableMediaError,
  mediaSrcMatches,
  safeAudioPlay,
} from '../../utils/audioPlaybackReady';

interface AudioPlayerProps {
  src: string;
  autoPlay?: boolean;
  controls?: boolean;
  loop?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
  className?: string;
  style?: React.CSSProperties;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onError?: (error: string) => void;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  showProgress?: boolean;
  showTime?: boolean;
  showVolume?: boolean;
  compact?: boolean;
  isDownloadable?: boolean;
  /** When set (e.g. admin preview), use mobile control layout below this width instead of viewport sm breakpoint */
  forceMobileLayout?: boolean;
}

interface AudioState {
  isPlaying: boolean;
  isLoading: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  error: string | null;
  isReady: boolean;
}

const DOWNLOAD_BLOCKED_BY_ADMIN_KEY = 'audioPlayer.downloadBlockedByAdmin';
const DOWNLOAD_WAITING_TO_LOAD_KEY = 'audioPlayer.downloadWaitingToLoad';

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  autoPlay = false,
  controls = true,
  loop = false,
  preload = 'metadata',
  className = '',
  style = {},
  onPlay,
  onPause,
  onEnded,
  onError,
  onLoadStart,
  onLoadEnd,
  onTimeUpdate,
  showProgress = true,
  showTime = true,
  showVolume = true,
  compact = false,
  isDownloadable = false,
  forceMobileLayout = false,
}) => {
  const { t } = useTranslation('learning');
  const useMobileControls = forceMobileLayout;
  const audioRef = useRef<HTMLAudioElement>(null);
  const appliedSrcRef = useRef<string>('');
  const loadAttemptRef = useRef(0);
  const loadRetryTimerRef = useRef<number | null>(null);
  const progressRef = useRef<HTMLInputElement>(null);
  const volumeRef = useRef<HTMLInputElement>(null);
  const speedDropdownRefMobile = useRef<HTMLDivElement>(null);
  const speedDropdownRefDesktop = useRef<HTMLDivElement>(null);
  const downloadHintTimerRef = useRef<number | null>(null);

  const [audioState, setAudioState] = useState<AudioState>({
    isPlaying: false,
    isLoading: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    isMuted: false,
    error: null,
    isReady: false,
  });
  const [userVizOpen, setUserVizOpen] = useState(false);
  const [showDownloadHint, setShowDownloadHint] = useState(false);

  // Format time for display
  const formatTime = useCallback((time: number): string => {
    if (isNaN(time)) return '0:00';
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Handle play/pause toggle
  const togglePlayPause = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !src?.trim()) return;

    try {
      if (audioState.isPlaying) {
        audio.pause();
      } else {
        if (audio.muted) {
          audio.muted = false;
        }
        if (audio.volume === 0) {
          audio.volume = audioState.volume || 1;
        }

        if (userVizOpen) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }

        setAudioState((prev) => ({ ...prev, isLoading: true, error: null }));
        await safeAudioPlay(audio);
        setAudioState((prev) => ({ ...prev, isLoading: false, isReady: true }));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Playback failed';
      setAudioState((prev) => ({ ...prev, error: errorMessage, isLoading: false }));
      onError?.(errorMessage);
    }
  }, [audioState.isPlaying, audioState.volume, onError, src, userVizOpen]);

  // Handle progress change
  const handleProgressChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;

    const newTime = parseFloat(event.target.value);
    audioRef.current.currentTime = newTime;
    setAudioState(prev => ({ ...prev, currentTime: newTime }));
  }, []);

  // Handle volume change
  const handleVolumeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current) return;

    const newVolume = parseFloat(event.target.value);
    audioRef.current.volume = newVolume;
    setAudioState(prev => ({ ...prev, volume: newVolume, isMuted: newVolume === 0 }));
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (!audioRef.current) return;

    const newMuted = !audioState.isMuted;
    audioRef.current.muted = newMuted;
    setAudioState(prev => ({ 
      ...prev, 
      isMuted: newMuted,
      volume: newMuted ? 0 : prev.volume || 1
    }));
  }, [audioState.isMuted]);

  // Skip backward (3 seconds)
  const handleSkipBackward = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 3);
  }, []);

  // Skip forward (3 seconds)
  const handleSkipForward = useCallback(() => {
    if (!audioRef.current) return;
    const newTime = Math.min(
      audioState.duration,
      audioRef.current.currentTime + 3
    );
    audioRef.current.currentTime = newTime;
  }, [audioState.duration]);

  // Toggle repeat - initialize from loop prop but allow user to toggle
  const [isRepeating, setIsRepeating] = useState(loop);
  const toggleRepeat = useCallback(() => {
    const newValue = !isRepeating;
    setIsRepeating(newValue);
    if (audioRef.current) {
      audioRef.current.loop = newValue;
    }
  }, [isRepeating]);

  // Handle stop
  const handleStop = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setAudioState(prev => ({ ...prev, isPlaying: false, currentTime: 0 }));
  }, []);

  // Playback speed control
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedDropdown, setShowSpeedDropdown] = useState(false);
  const speedOptions = [2, 1.5, 1, 0.7, 0.5];
  
  const handleSpeedClick = useCallback(() => {
    // Cycle to next speed
    const currentIndex = speedOptions.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % speedOptions.length;
    const newSpeed = speedOptions[nextIndex];
    setPlaybackSpeed(newSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }
    // Open dropdown
    setShowSpeedDropdown(true);
  }, [playbackSpeed]);

  const handleSpeedSelect = useCallback((speed: number) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
    setShowSpeedDropdown(false);
  }, []);

  // Close dropdown when clicking outside (mobile + desktop control copies each have a ref)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inMobile = speedDropdownRefMobile.current?.contains(target);
      const inDesktop = speedDropdownRefDesktop.current?.contains(target);
      if (!inMobile && !inDesktop) {
        setShowSpeedDropdown(false);
      }
    };

    if (showSpeedDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSpeedDropdown]);

  // Handle download
  const handleDownload = useCallback(() => {
    if (!src || !isDownloadable) return;
    const link = document.createElement('a');
    link.href = src;
    link.download = src.split('/').pop() || 'audio';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [src, isDownloadable]);

  // Sync loop prop with state when prop changes (but don't override user toggle)
  useEffect(() => {
    if (audioRef.current && loop !== isRepeating) {
      setIsRepeating(loop);
      audioRef.current.loop = loop;
    }
  }, [loop]); // Only sync when prop changes, not when state changes

  // Set initial playback speed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  const clearLoadRetryTimer = useCallback(() => {
    if (loadRetryTimerRef.current !== null) {
      window.clearTimeout(loadRetryTimerRef.current);
      loadRetryTimerRef.current = null;
    }
  }, []);

  const beginAudioLoad = useCallback((audio: HTMLAudioElement, trimmed: string, forceReload: boolean) => {
    clearLoadRetryTimer();
    applyAudioCrossOrigin(audio, trimmed);
    setAudioState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
      isReady: false,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
    }));

    const alreadySame = mediaSrcMatches(audio.src, trimmed);
    if (!alreadySame) {
      audio.src = trimmed;
    }
    if (!alreadySame || forceReload || audio.error || audio.readyState < HTMLMediaElement.HAVE_METADATA) {
      audio.load();
    }
  }, [clearLoadRetryTimer]);

  // Update audio src when prop changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const trimmed = src?.trim() ?? '';
    if (!trimmed) {
      clearLoadRetryTimer();
      loadAttemptRef.current = 0;
      appliedSrcRef.current = '';
      setAudioState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'No audio source provided',
        isReady: false,
        isPlaying: false,
        currentTime: 0,
        duration: 0,
      }));
      audio.removeAttribute('src');
      audio.load();
      return;
    }

    const sameSrc = mediaSrcMatches(audio.src, trimmed) && appliedSrcRef.current === trimmed;
    if (sameSrc) {
      // Strict-mode / remount races can miss canplay/error. Recover instead of bailing.
      if (audio.error) {
        loadAttemptRef.current = 0;
        beginAudioLoad(audio, trimmed, true);
        return;
      }
      if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
        setAudioState((prev) => ({
          ...prev,
          duration: Number.isFinite(audio.duration) ? audio.duration : prev.duration,
          isReady: true,
          isLoading: false,
          error: null,
        }));
      }
      return;
    }

    loadAttemptRef.current = 0;
    appliedSrcRef.current = trimmed;
    beginAudioLoad(audio, trimmed, false);
  }, [src, beginAudioLoad, clearLoadRetryTimer]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadStart = () => {
      setAudioState(prev => ({ ...prev, isLoading: true, error: null }));
      onLoadStart?.();
    };

    const handleLoadedMetadata = () => {
      loadAttemptRef.current = 0;
      clearLoadRetryTimer();
      setAudioState(prev => ({ 
        ...prev, 
        duration: audio.duration,
        isReady: true,
        isLoading: false,
        error: null,
      }));
      onLoadEnd?.();
    };

    const handleCanPlay = () => {
      loadAttemptRef.current = 0;
      clearLoadRetryTimer();
      setAudioState(prev => ({ ...prev, isLoading: false, isReady: true, error: null }));
    };

    const handlePlay = () => {
      setAudioState(prev => ({ ...prev, isPlaying: true }));
      onPlay?.();
    };

    const handlePause = () => {
      setAudioState(prev => ({ ...prev, isPlaying: false }));
      onPause?.();
    };

    const handleTimeUpdate = () => {
      const currentTime = audio.currentTime;
      const duration = audio.duration;
      
      setAudioState(prev => ({ ...prev, currentTime }));
      onTimeUpdate?.(currentTime, duration);
    };

    const handleEnded = () => {
      setAudioState(prev => ({ ...prev, isPlaying: false, currentTime: 0 }));
      onEnded?.();
    };

    const handleError = () => {
      const audioElement = audioRef.current;
      const mediaError = audioElement?.error ?? null;
      const currentSrc = appliedSrcRef.current || src;

      if (
        audioElement &&
        currentSrc &&
        isRetryableMediaError(mediaError) &&
        loadAttemptRef.current < AUDIO_LOAD_MAX_ATTEMPTS - 1
      ) {
        loadAttemptRef.current += 1;
        const attempt = loadAttemptRef.current;
        setAudioState((prev) => ({
          ...prev,
          isLoading: true,
          error: null,
          isReady: false,
          isPlaying: false,
        }));
        clearLoadRetryTimer();
        loadRetryTimerRef.current = window.setTimeout(() => {
          const el = audioRef.current;
          if (!el || !mediaSrcMatches(el.src, currentSrc)) return;
          applyAudioCrossOrigin(el, currentSrc);
          el.load();
        }, audioLoadRetryDelayMs(attempt));
        return;
      }

      let errorMessage = 'Failed to load audio file';
      
      if (mediaError) {
        switch (mediaError.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            errorMessage = 'Audio loading was aborted';
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            errorMessage = 'Network error while loading audio';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorMessage = 'Audio decoding error';
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage = `Audio format not supported or file not found: ${currentSrc}`;
            break;
          default:
            errorMessage = `Failed to load audio: ${currentSrc}`;
        }
      }
      
      console.error('AudioPlayer error:', {
        src: currentSrc,
        error: mediaError,
        networkState: audioElement?.networkState,
        readyState: audioElement?.readyState,
        attempts: loadAttemptRef.current + 1,
      });
      
      setAudioState(prev => ({ 
        ...prev, 
        error: errorMessage, 
        isLoading: false, 
        isPlaying: false 
      }));
      onError?.(errorMessage);
    };

    const handleVolumeChange = () => {
      setAudioState(prev => ({ 
        ...prev, 
        volume: audio.volume, 
        isMuted: audio.muted 
      }));
    };

    // Add event listeners
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('volumechange', handleVolumeChange);

    // Recover ready state if metadata arrived while listeners were detached (Strict Mode).
    // Errors are retried by the src effect / error handler — do not re-enter handleError here
    // or Strict Mode cleanup/reattach will burn the retry budget.
    if (!audio.error && audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
      handleLoadedMetadata();
    }

    // Cleanup
    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [onPlay, onPause, onEnded, onError, onLoadStart, onLoadEnd, onTimeUpdate, src, clearLoadRetryTimer]);

  useEffect(() => () => clearLoadRetryTimer(), [clearLoadRetryTimer]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.target !== document.body) return;

      switch (event.code) {
        case 'Space':
          event.preventDefault();
          togglePlayPause();
          break;
        case 'ArrowLeft':
          if (audioRef.current) {
            audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 3);
          }
          break;
        case 'ArrowRight':
          if (audioRef.current) {
            audioRef.current.currentTime = Math.min(
              audioState.duration, 
              audioRef.current.currentTime + 3
            );
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [togglePlayPause, audioState.duration]);

  const containerClassName = `audio-player ${compact ? 'audio-player-compact' : ''} ${className} ${
    compact ? 'p-1.5 sm:p-2' : 'p-2 sm:p-3'
  } max-w-none self-stretch w-full min-w-0`.trim();

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    backgroundColor: 'var(--audio-bg, #ffffff)',
    borderRadius: '0.75rem',
    border: '1px solid var(--audio-border, #e5e7eb)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: compact ? '0.875rem' : '0.9375rem',
    width: '100%',
    maxWidth: 'none',
    boxSizing: 'border-box',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    ...style,
  };

  const buttonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: audioState.isReady ? 'pointer' : 'not-allowed',
    padding: '0.25rem',
    borderRadius: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: audioState.isReady ? 1 : 0.5,
  };

  const progressStyle: React.CSSProperties = {
    flex: 1,
    height: '0.25rem',
    background: '#e2e8f0',
    borderRadius: '0.125rem',
    outline: 'none',
    cursor: 'pointer',
  };

  const volumeStyle: React.CSSProperties = {
    width: compact ? '60px' : '80px',
    height: '0.25rem',
    background: '#e2e8f0',
    borderRadius: '0.125rem',
    outline: 'none',
    cursor: 'pointer',
  };

  const downloadAllowed = isDownloadable && audioState.isReady;
  const downloadTooltip = !isDownloadable
    ? t(DOWNLOAD_BLOCKED_BY_ADMIN_KEY)
    : !audioState.isReady
      ? t(DOWNLOAD_WAITING_TO_LOAD_KEY)
      : t('audioPlayer.downloadAudio');

  const audioTooltips = useMemo(
    () => ({
      play: t('audioPlayer.play'),
      pause: t('audioPlayer.pause'),
      stop: t('audioPlayer.stop'),
      showWaveform: t('audioPlayer.showWaveform'),
      hideWaveform: t('audioPlayer.hideWaveform'),
      skipBackward3: t('audioPlayer.skipBackward3'),
      skipForward3: t('audioPlayer.skipForward3'),
      enableRepeat: t('audioPlayer.enableRepeat'),
      disableRepeat: t('audioPlayer.disableRepeat'),
      mute: t('audioPlayer.mute'),
      unmute: t('audioPlayer.unmute'),
      playbackSpeed: (speed: number) => t('audioPlayer.playbackSpeed', { speed }),
    }),
    [t]
  );
  const downloadHintWrapperClass = !downloadAllowed
    ? 'cursor-help hover:bg-gray-200/40 dark:hover:bg-gray-600/30'
    : '';
  const showDownloadDisabledHint = useCallback((event: React.MouseEvent<HTMLSpanElement>) => {
    if (downloadAllowed) return;
    event.preventDefault();
    event.stopPropagation();
    setShowDownloadHint(true);
    if (downloadHintTimerRef.current !== null) {
      window.clearTimeout(downloadHintTimerRef.current);
    }
    downloadHintTimerRef.current = window.setTimeout(() => {
      setShowDownloadHint(false);
    }, 2200);
  }, [downloadAllowed]);

  useEffect(() => {
    return () => {
      if (downloadHintTimerRef.current !== null) {
        window.clearTimeout(downloadHintTimerRef.current);
      }
    };
  }, []);

  return (
    <div className={containerClassName} style={containerStyle} dir="ltr">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        autoPlay={autoPlay}
        loop={loop}
        preload={preload}
        style={{ display: 'none' }}
      />

      {!compact && (
        <div
          className={`overflow-hidden transition-[max-height,opacity,margin] duration-300 ease-in-out ${
            userVizOpen
              ? 'max-h-[88px] opacity-100 mb-2'
              : 'max-h-0 opacity-0 mb-0 pointer-events-none'
          }`}
        >
          <div className="w-full">
            <AudioVisualizer
              audioElement={audioRef.current}
              isPlaying={audioState.isPlaying}
              height={60}
              barCount={32}
            />
          </div>
        </div>
      )}

      {/* Controls: two-line layout on narrow viewports, single row from sm */}
      {!compact && (
        <>
          <div className={`flex flex-col gap-2 w-full min-w-0 ${useMobileControls ? '' : 'sm:hidden'}`}>
            <div className="flex w-full min-w-0 items-center gap-1">
              {controls && (
                <button
                  onClick={togglePlayPause}
                  disabled={!audioState.isReady}
                  className="flex shrink-0 items-center justify-center w-9 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white transition-all duration-200 shadow-sm"
                  title={audioState.isPlaying ? audioTooltips.pause : audioTooltips.play}
                  aria-label={audioState.isPlaying ? audioTooltips.pause : audioTooltips.play}
                >
                  {audioState.isLoading ? (
                    <span className="text-sm">⏳</span>
                  ) : audioState.isPlaying ? (
                    <span className="text-sm">⏸</span>
                  ) : (
                    <span className="text-sm">▶</span>
                  )}
                </button>
              )}
              {controls && (
                <button
                  onClick={handleStop}
                  disabled={!audioState.isReady}
                  className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-200"
                  title={audioTooltips.stop}
                  aria-label={audioTooltips.stop}
                >
                  <span className="text-sm">⏹</span>
                </button>
              )}
              {controls && (
                <button
                  type="button"
                  onClick={() => setUserVizOpen((v) => !v)}
                  className={`flex shrink-0 items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 hover:scale-105 ${
                    userVizOpen
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                  title={userVizOpen ? audioTooltips.hideWaveform : audioTooltips.showWaveform}
                  aria-label={userVizOpen ? 'Hide audio visualization' : 'Show audio visualization'}
                >
                  <Activity className="w-4 h-4" aria-hidden />
                </button>
              )}
              {showTime && (
                <span className="min-w-[2.25rem] text-[0.7rem] tabular-nums text-gray-600 dark:text-gray-400 shrink-0 font-medium">
                  {formatTime(audioState.currentTime)}
                </span>
              )}
              {showProgress && (
                <input
                  ref={progressRef}
                  type="range"
                  min="0"
                  max={audioState.duration || 0}
                  value={audioState.currentTime}
                  onChange={handleProgressChange}
                  disabled={!audioState.isReady}
                  className="flex-1 min-w-0 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(audioState.currentTime / (audioState.duration || 1)) * 100}%, #e5e7eb ${(audioState.currentTime / (audioState.duration || 1)) * 100}%, #e5e7eb 100%)`,
                    WebkitAppearance: 'none',
                    appearance: 'none',
                  }}
                  aria-label="Audio progress"
                />
              )}
              {showTime && (
                <span className="min-w-[2.25rem] text-[0.7rem] tabular-nums text-gray-600 dark:text-gray-400 shrink-0 font-medium">
                  {formatTime(audioState.duration)}
                </span>
              )}
            </div>
            <div className="flex w-full min-w-0 items-center gap-1.5 pb-0.5">
                <div className="flex shrink-0 items-center gap-1.5">
                {controls && (
                  <button
                    onClick={handleSkipBackward}
                    disabled={!audioState.isReady}
                    className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-200"
                    title={audioTooltips.skipBackward3}
                    aria-label="Skip backward 3 seconds"
                  >
                    <span className="text-sm">⏪</span>
                  </button>
                )}
                {controls && (
                  <button
                    onClick={handleSkipForward}
                    disabled={!audioState.isReady}
                    className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-200"
                    title={audioTooltips.skipForward3}
                    aria-label="Skip forward 3 seconds"
                  >
                    <span className="text-sm">⏩</span>
                  </button>
                )}
                {controls && (
                  <button
                    onClick={toggleRepeat}
                    disabled={!audioState.isReady}
                    className={`flex shrink-0 items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
                      isRepeating
                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                        : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                    title={isRepeating ? audioTooltips.disableRepeat : audioTooltips.enableRepeat}
                    aria-label={isRepeating ? 'Disable repeat' : 'Enable repeat'}
                  >
                    <span className="text-sm">🔁</span>
                  </button>
                )}
                </div>
                {showVolume && (
                  <div className="flex min-w-0 flex-1 items-center gap-1">
                    <button
                      onClick={toggleMute}
                      className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-all duration-200"
                      title={audioState.isMuted ? audioTooltips.unmute : audioTooltips.mute}
                      aria-label={audioState.isMuted ? 'Unmute' : 'Mute'}
                    >
                      <span className="text-sm">{audioState.isMuted ? '🔇' : '🔊'}</span>
                    </button>
                    <input
                      ref={volumeRef}
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={audioState.isMuted ? 0 : audioState.volume}
                      onChange={handleVolumeChange}
                      className="h-1.5 min-w-0 flex-1 rounded-lg appearance-none cursor-pointer bg-gray-200 dark:bg-gray-700"
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(audioState.isMuted ? 0 : audioState.volume) * 100}%, #e5e7eb ${(audioState.isMuted ? 0 : audioState.volume) * 100}%, #e5e7eb 100%)`,
                        WebkitAppearance: 'none',
                        appearance: 'none',
                      }}
                      aria-label="Volume"
                    />
                  </div>
                )}
                <div className="flex shrink-0 items-center gap-1.5">
                {controls && (
                  <div className="relative shrink-0" ref={speedDropdownRefMobile}>
                    <button
                      onClick={handleSpeedClick}
                      disabled={!audioState.isReady}
                      className={`flex items-center justify-center min-w-[2.75rem] h-8 px-2 rounded-lg font-medium text-xs transition-all duration-200 ${
                        playbackSpeed !== 1
                          ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                          : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                      } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                      title={audioTooltips.playbackSpeed(playbackSpeed)}
                      aria-label={`Playback speed: ${playbackSpeed}x`}
                    >
                      <span>{playbackSpeed}x</span>
                    </button>
                    {showSpeedDropdown && (
                      <div className="absolute bottom-full left-0 mb-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 min-w-[5rem]">
                        {speedOptions.map((speed) => (
                          <button
                            key={speed}
                            onClick={() => handleSpeedSelect(speed)}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                              playbackSpeed === speed
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {speed}x
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <span
                  className={`relative inline-flex shrink-0 rounded-lg ${downloadHintWrapperClass}`}
                  title={downloadTooltip}
                  onClick={showDownloadDisabledHint}
                >
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={!downloadAllowed}
                    className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
                      downloadAllowed
                        ? 'bg-gray-100 hover:bg-gray-200 hover:scale-105 active:scale-95 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                        : 'pointer-events-none bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-600 opacity-60'
                    }`}
                    aria-label={downloadTooltip}
                  >
                    <Download className="w-4 h-4" aria-hidden />
                  </button>
                  {showDownloadHint && !downloadAllowed && (
                    <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-56 rounded-md bg-gray-900/95 px-2 py-1 text-[11px] leading-4 text-white shadow-lg">
                      {downloadTooltip}
                    </span>
                  )}
                </span>
                </div>
            </div>
          </div>

          <div className={`${useMobileControls ? 'hidden' : 'hidden sm:flex'} sm:flex-row sm:items-center sm:flex-nowrap gap-2 w-full min-w-0`}>
            <div className="flex-1 flex items-center gap-1 min-w-0">
              {controls && (
                <button
                  onClick={togglePlayPause}
                  disabled={!audioState.isReady}
                  className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white transition-all duration-200 hover:scale-105 shadow-sm"
                  title={audioState.isPlaying ? audioTooltips.pause : audioTooltips.play}
                  aria-label={audioState.isPlaying ? audioTooltips.pause : audioTooltips.play}
                >
                  {audioState.isLoading ? (
                    <span className="text-sm">⏳</span>
                  ) : audioState.isPlaying ? (
                    <span className="text-sm">⏸</span>
                  ) : (
                    <span className="text-sm">▶</span>
                  )}
                </button>
              )}
              {controls && (
                <button
                  onClick={handleStop}
                  disabled={!audioState.isReady}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                  title={audioTooltips.stop}
                  aria-label={audioTooltips.stop}
                >
                  <span className="text-sm">⏹</span>
                </button>
              )}
              {controls && (
                <button
                  type="button"
                  onClick={() => setUserVizOpen((v) => !v)}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 hover:scale-105 ${
                    userVizOpen
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                  title={userVizOpen ? audioTooltips.hideWaveform : audioTooltips.showWaveform}
                  aria-label={userVizOpen ? 'Hide audio visualization' : 'Show audio visualization'}
                >
                  <Activity className="w-4 h-4" aria-hidden />
                </button>
              )}
              {controls && (
                <button
                  onClick={handleSkipBackward}
                  disabled={!audioState.isReady}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                  title={audioTooltips.skipBackward3}
                  aria-label="Skip backward 3 seconds"
                >
                  <span className="text-sm">⏪</span>
                </button>
              )}
              {showTime && (
                <span className="min-w-[2.5rem] text-xs text-gray-600 dark:text-gray-400 flex-shrink-0 font-medium">
                  {formatTime(audioState.currentTime)}
                </span>
              )}
              {showProgress && (
                <input
                  type="range"
                  min="0"
                  max={audioState.duration || 0}
                  value={audioState.currentTime}
                  onChange={handleProgressChange}
                  disabled={!audioState.isReady}
                  className="flex-1 min-w-0 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(audioState.currentTime / (audioState.duration || 1)) * 100}%, #e5e7eb ${(audioState.currentTime / (audioState.duration || 1)) * 100}%, #e5e7eb 100%)`,
                    WebkitAppearance: 'none',
                    appearance: 'none',
                  }}
                  aria-label="Audio progress"
                />
              )}
              {showTime && (
                <span className="min-w-[2.5rem] text-xs text-gray-600 dark:text-gray-400 flex-shrink-0 font-medium">
                  {formatTime(audioState.duration)}
                </span>
              )}
              {controls && (
                <button
                  onClick={handleSkipForward}
                  disabled={!audioState.isReady}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                  title={audioTooltips.skipForward3}
                  aria-label="Skip forward 3 seconds"
                >
                  <span className="text-sm">⏩</span>
                </button>
              )}
              {controls && (
                <button
                  onClick={toggleRepeat}
                  disabled={!audioState.isReady}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 hover:scale-105 ${
                    isRepeating
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  title={isRepeating ? audioTooltips.disableRepeat : audioTooltips.enableRepeat}
                  aria-label={isRepeating ? 'Disable repeat' : 'Enable repeat'}
                >
                  <span className="text-sm">🔁</span>
                </button>
              )}
            </div>
            {showVolume && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={toggleMute}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                  title={audioState.isMuted ? audioTooltips.unmute : audioTooltips.mute}
                  aria-label={audioState.isMuted ? 'Unmute' : 'Mute'}
                >
                  <span className="text-sm">{audioState.isMuted ? '🔇' : '🔊'}</span>
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={audioState.isMuted ? 0 : audioState.volume}
                  onChange={handleVolumeChange}
                  className="h-1.5 w-14 shrink-0 rounded-lg appearance-none cursor-pointer bg-gray-200 dark:bg-gray-700 sm:w-16"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(audioState.isMuted ? 0 : audioState.volume) * 100}%, #e5e7eb ${(audioState.isMuted ? 0 : audioState.volume) * 100}%, #e5e7eb 100%)`,
                    WebkitAppearance: 'none',
                    appearance: 'none',
                  }}
                  aria-label="Volume"
                />
                {controls && (
                  <div className="relative" ref={speedDropdownRefDesktop}>
                    <button
                      onClick={handleSpeedClick}
                      disabled={!audioState.isReady}
                      className={`flex items-center justify-center min-w-[3rem] h-8 px-2 rounded-lg font-medium text-xs transition-all duration-200 hover:scale-105 ${
                        playbackSpeed !== 1
                          ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                          : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                      } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                      title={audioTooltips.playbackSpeed(playbackSpeed)}
                      aria-label={`Playback speed: ${playbackSpeed}x`}
                    >
                      <span>{playbackSpeed}x</span>
                    </button>
                    {showSpeedDropdown && (
                      <div className="absolute bottom-full left-0 mb-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 min-w-[5rem]">
                        {speedOptions.map((speed) => (
                          <button
                            key={speed}
                            onClick={() => handleSpeedSelect(speed)}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                              playbackSpeed === speed
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {speed}x
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <span
                  className={`relative inline-flex rounded-lg ${downloadHintWrapperClass}`}
                  title={downloadTooltip}
                  onClick={showDownloadDisabledHint}
                >
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={!downloadAllowed}
                    className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
                      downloadAllowed
                        ? 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 hover:scale-105 active:scale-95'
                        : 'pointer-events-none bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-600 opacity-60'
                    }`}
                    aria-label={downloadTooltip}
                  >
                    <Download className="w-4 h-4" aria-hidden />
                  </button>
                  {showDownloadHint && !downloadAllowed && (
                    <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-56 rounded-md bg-gray-900/95 px-2 py-1 text-[11px] leading-4 text-white shadow-lg">
                      {downloadTooltip}
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {compact && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2 w-full">
          <div className="flex-1 flex items-center gap-1 min-w-0">
            {controls && (
              <button
                onClick={togglePlayPause}
                disabled={!audioState.isReady}
                className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white transition-all duration-200 hover:scale-105 shadow-sm"
                title={audioState.isPlaying ? audioTooltips.pause : audioTooltips.play}
                aria-label={audioState.isPlaying ? audioTooltips.pause : audioTooltips.play}
              >
                {audioState.isLoading ? (
                  <span className="text-sm">⏳</span>
                ) : audioState.isPlaying ? (
                  <span className="text-sm">⏸</span>
                ) : (
                  <span className="text-sm">▶</span>
                )}
              </button>
            )}
            {controls && (
              <button
                onClick={handleStop}
                disabled={!audioState.isReady}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                title={audioTooltips.stop}
                aria-label={audioTooltips.stop}
              >
                <span className="text-sm">⏹</span>
              </button>
            )}
            {controls && (
              <button
                onClick={handleSkipBackward}
                disabled={!audioState.isReady}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                title={audioTooltips.skipBackward3}
                aria-label={audioTooltips.skipBackward3}
              >
                <span className="text-sm">⏪</span>
              </button>
            )}
            {showProgress && (
              <input
                type="range"
                min="0"
                max={audioState.duration || 0}
                value={audioState.currentTime}
                onChange={handleProgressChange}
                disabled={!audioState.isReady}
                className="flex-1 min-w-0 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(audioState.currentTime / (audioState.duration || 1)) * 100}%, #e5e7eb ${(audioState.currentTime / (audioState.duration || 1)) * 100}%, #e5e7eb 100%)`,
                  WebkitAppearance: 'none',
                  appearance: 'none',
                }}
                aria-label="Audio progress"
              />
            )}
            {controls && (
              <button
                onClick={handleSkipForward}
                disabled={!audioState.isReady}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                title={audioTooltips.skipForward3}
                aria-label={audioTooltips.skipForward3}
              >
                <span className="text-sm">⏩</span>
              </button>
            )}
            {controls && (
              <button
                onClick={toggleRepeat}
                disabled={!audioState.isReady}
                className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 hover:scale-105 ${
                  isRepeating
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                    : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                title={isRepeating ? audioTooltips.disableRepeat : audioTooltips.enableRepeat}
                aria-label={isRepeating ? audioTooltips.disableRepeat : audioTooltips.enableRepeat}
              >
                <span className="text-sm">🔁</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {audioState.error && (
        <span style={{ color: 'var(--audio-error, #e53e3e)', fontSize: '0.875rem' }}>
          ❌ {audioState.error}
        </span>
      )}
    </div>
  );
};

export default AudioPlayer;